// Wall rendering utilities

import { gridPointToPixel } from './coordinate-transforms.js';

/**
 * @typedef {import('../types/jsdoc-types.js').Wall} Wall
 * @typedef {import('../types/jsdoc-types.js').GridPoint} GridPoint
 */

/**
 * @typedef {object} Point
 * @property {number} x
 * @property {number} y
 */

const WALL_COLOR = '#ff0000';
const VISION_WALL_COLOR = '#00ff00';

/**
 * Draw a wall segment
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Point>} points - Pixel coordinates
 * @param {string} color - Wall color
 * @param {number} zoom - Zoom level
 * @param {boolean} [showCircles=true] - Whether to show control point circles
 */
export function drawWallSegment(ctx, points, color, zoom, showCircles = true) {
  if (points.length < 1) return;

  // Draw lines between points
  if (points.length > 1) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 6 / zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      if (!point) continue;
      if (i === 0) {
        ctx.moveTo(point.x, point.y);
      } else {
        ctx.lineTo(point.x, point.y);
      }
    }
    ctx.stroke();
  }

  // Draw control point circles
  if (showCircles) {
    for (const point of points) {
      ctx.fillStyle = color;
      const circleRadius = 8 / zoom;
      ctx.beginPath();
      ctx.arc(point.x, point.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
}

/**
 * Draw all walls
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Wall>} walls
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {boolean} [options.isEditorMode=false] - Whether in editor mode (shows control points)
 * @param {number|null} [options.selectedWallIndex=null]
 * @param {number|null} [options.hoveredWallIndex=null]
 * @param {string} [options.wallColor] - Override wall color
 * @param {boolean} [options.showWalls=true] - Whether to show walls
 */
export function drawWalls(ctx, walls, options) {
  const {
    gridSize,
    zoom,
    isEditorMode = false,
    selectedWallIndex = null,
    hoveredWallIndex = null,
    wallColor = WALL_COLOR,
    showWalls = true,
  } = options;

  // Only show walls in editor mode or if showWalls is true
  if (!isEditorMode && !showWalls) return;

  for (let i = 0; i < walls.length; i++) {
    const wall = walls[i];
    if (!wall) continue;

    if (wall.points && wall.points.length > 0) {
      // Convert grid coordinates to pixels
      const pixelPoints = wall.points.map((gp) => gridPointToPixel(gp, gridSize));

      // Choose color based on wall type
      const color = wall.blocksVision ? wallColor : VISION_WALL_COLOR;

      // Determine rendering style
      let showCircles = false;
      let alpha = 0.5;

      if (isEditorMode) {
        if (i === selectedWallIndex || i === hoveredWallIndex) {
          alpha = 1.0;
          showCircles = true;
        }
      }

      ctx.globalAlpha = alpha;
      drawWallSegment(ctx, pixelPoints, color, zoom, showCircles);
      ctx.globalAlpha = 1.0;
    }
  }
}

/**
 * Draw wall preview while drawing
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<GridPoint>} currentWall - Points of wall being drawn
 * @param {Point|null} previewPoint - Preview point (pixel coordinates)
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {string} [options.wallColor]
 * @param {(from: Point, to: Point) => boolean} [options.isValidDiagonal] - Validation function
 */
export function drawWallPreview(ctx, currentWall, previewPoint, options) {
  const { gridSize, zoom, wallColor = WALL_COLOR, isValidDiagonal } = options;

  if (!currentWall || currentWall.length === 0) return;

  const pixelPoints = currentWall.map((gp) => gridPointToPixel(gp, gridSize));
  drawWallSegment(ctx, pixelPoints, wallColor, zoom, true);

  // Draw preview line to cursor
  if (previewPoint && currentWall.length > 0) {
    const lastGridPoint = currentWall[currentWall.length - 1];
    if (!lastGridPoint) return;
    const lastPoint = gridPointToPixel(lastGridPoint, gridSize);
    const isValid = !isValidDiagonal || isValidDiagonal(lastPoint, previewPoint);

    ctx.strokeStyle = wallColor;
    ctx.globalAlpha = isValid ? 1.0 : 0.3;
    ctx.lineWidth = 6 / zoom;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPoint.x, lastPoint.y);
    ctx.lineTo(previewPoint.x, previewPoint.y);
    ctx.stroke();
    ctx.globalAlpha = 1.0;

    // Draw preview point
    if (isValid) {
      ctx.fillStyle = wallColor;
      const circleRadius = 8 / zoom;
      ctx.beginPath();
      ctx.arc(previewPoint.x, previewPoint.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();
    } else {
      // Draw "not allowed" icon
      const circleRadius = 8 / zoom;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(previewPoint.x, previewPoint.y, circleRadius, 0, 2 * Math.PI);
      ctx.fill();

      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 2 / zoom;
      ctx.beginPath();
      ctx.arc(previewPoint.x, previewPoint.y, circleRadius, 0, 2 * Math.PI);
      ctx.stroke();

      const slashRadius = circleRadius * 0.7;
      ctx.beginPath();
      ctx.moveTo(previewPoint.x - slashRadius, previewPoint.y - slashRadius);
      ctx.lineTo(previewPoint.x + slashRadius, previewPoint.y + slashRadius);
      ctx.stroke();
    }
  }
}

/**
 * Draw walls for player view with per-segment visibility clipping.
 * Only draws wall segments adjacent to visible cells.
 * @param {CanvasRenderingContext2D} ctx
 * @param {Array<Wall>} walls - All walls from mapState
 * @param {object} options
 * @param {number} options.gridSize
 * @param {number} options.zoom
 * @param {Array<{col: number, row: number, illumination: string, visibility?: string}>} [options.visibleGridCells]
 */
export function drawVisibleWalls(ctx, walls, options) {
  const { gridSize, zoom, visibleGridCells } = options;

  // Build a visibility lookup set. The original filter required
  // illumination !== 'none', which excluded the immediately-adjacent
  // sense special-case (#253) — those cells carry Illumination=none
  // but Visibility=adjacent, and the player perceives them. The
  // toggle-walls debug overlay (#352) needs walls drawn around
  // perceived adjacent cells too. Either condition counts as
  // "perceived" for wall-drawing purposes; cells truly invisible to
  // the player are absent from visibleGridCells entirely.
  const visibleSet = new Set();
  if (visibleGridCells) {
    for (const cell of visibleGridCells) {
      if (cell.illumination !== 'none' || cell.visibility === 'adjacent') {
        visibleSet.add(`${cell.col},${cell.row}`);
      }
    }
  }
  const noVisibility = visibleSet.size === 0 && !visibleGridCells;

  ctx.globalAlpha = 0.5;

  for (const wall of walls) {
    if (!wall.points || wall.points.length < 2) continue;

    const color = wall.blocksVision ? '#ff0000' : '#00ff00';

    for (let i = 0; i < wall.points.length - 1; i++) {
      const start = wall.points[i];
      const end = wall.points[i + 1];
      if (!start || !end) continue;

      const dx = end.x - start.x;
      const dy = end.y - start.y;

      const isHorizontal = dy === 0;
      const isVertical = dx === 0;

      if (isVertical) {
        const col = start.x;
        const minRow = Math.min(start.y, end.y);
        const maxRow = Math.max(start.y, end.y);

        for (let row = minRow; row < maxRow; row++) {
          if (noVisibility || visibleSet.has(`${col - 1},${row}`) || visibleSet.has(`${col},${row}`)) {
            const x = col * gridSize;
            const y1 = row * gridSize;
            const y2 = (row + 1) * gridSize;
            drawWallSegment(
              ctx,
              [
                { x, y: y1 },
                { x, y: y2 },
              ],
              color,
              zoom,
              false,
            );
          }
        }
      } else if (isHorizontal) {
        const row = start.y;
        const minCol = Math.min(start.x, end.x);
        const maxCol = Math.max(start.x, end.x);

        for (let col = minCol; col < maxCol; col++) {
          if (noVisibility || visibleSet.has(`${col},${row - 1}`) || visibleSet.has(`${col},${row}`)) {
            const y = row * gridSize;
            const x1 = col * gridSize;
            const x2 = (col + 1) * gridSize;
            drawWallSegment(
              ctx,
              [
                { x: x1, y },
                { x: x2, y },
              ],
              color,
              zoom,
              false,
            );
          }
        }
      } else {
        // Diagonal - break into 1-unit segments
        const steps = Math.max(Math.abs(dx), Math.abs(dy));
        const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
        const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0;

        for (let step = 0; step < steps; step++) {
          const curX = start.x + step * stepX;
          const curY = start.y + step * stepY;
          const nextX = curX + stepX;
          const nextY = curY + stepY;

          const cellCol = Math.min(curX, nextX);
          const cellRow = Math.min(curY, nextY);

          if (noVisibility || visibleSet.has(`${cellCol},${cellRow}`)) {
            drawWallSegment(
              ctx,
              [
                { x: curX * gridSize, y: curY * gridSize },
                { x: nextX * gridSize, y: nextY * gridSize },
              ],
              color,
              zoom,
              false,
            );
          }
        }
      }
    }
  }

  ctx.globalAlpha = 1.0;
}
